import { getMyNotifications, markNotificationAsRead } from '../api';
import { Notification, NotificationSettings, NotificationAnimationConfig, NotificationType } from '../types';
import darkGeneral from '../assets/dark-star.webm';
import brightGeneral from '../assets/bright-star.webm';

class NotificationService {
  private intervalId: NodeJS.Timeout | null = null;
  private lastNotificationIds: Set<string> = new Set();
  private settings: NotificationSettings;
  private animationConfigs: NotificationAnimationConfig[];
  private onNewNotification: ((notification: Notification) => void) | null = null;

  constructor() {
    // Default settings
    this.settings = {
      enabled: true,
      interval: 15, // 15 seconds
      animationEnabled: true,
      soundEnabled: false,
      theme: 'dark', // Default theme
      donationTextTemplate: '{{sender}} donated {{amount}} for {{challenge}}', // Default donation template
      challengeTextTemplate: '{{sender}} created {{challenge}} for {{amount}}', // Default challenge template
    };

    // Default animation configurations with light and dark themes
    this.animationConfigs = [
      {
        type: 'challenge',
        theme: 'light',
        animationFile: brightGeneral,
        duration: 10000,
      },
      {
        type: 'challenge',
        theme: 'dark',
        animationFile: darkGeneral,
        duration: 10000,
      },
      {
        type: 'donation',
        theme: 'light',
        animationFile: brightGeneral,
        duration: 10000,
      },
      {
        type: 'donation',
        theme: 'dark',
        animationFile: darkGeneral,
        duration: 10000,
      },
      {
        type: 'general',
        theme: 'light',
        animationFile: brightGeneral,
        duration: 7000,
      },
      {
        type: 'general',
        theme: 'dark',
        animationFile: darkGeneral,
        duration: 7000,
      },
    ];

    this.loadSettings();
  }

  private loadSettings(): void {
    const savedSettings = localStorage.getItem('notificationSettings');
    
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        this.settings = { ...this.settings, ...parsedSettings };
      } catch (error) {
        console.error('Error loading notification settings:', error);
      }
    }

    const savedAnimations = localStorage.getItem('notificationAnimations');
    if (savedAnimations) {
      try {
        this.animationConfigs = JSON.parse(savedAnimations);
      } catch (error) {
        console.error('Error loading notification animations:', error);
      }
    }
  }

  private saveSettings(): void {
    localStorage.setItem('notificationSettings', JSON.stringify(this.settings));
  }

  private saveAnimations(): void {
    localStorage.setItem('notificationAnimations', JSON.stringify(this.animationConfigs));
  }

  public setSettings(newSettings: Partial<NotificationSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();
    
    // Restart polling if interval changed
    if (newSettings.interval !== undefined) {
      this.stopPolling();
      if (this.settings.enabled) {
        this.startPolling();
      }
    }
  }

  public getSettings(): NotificationSettings {
    return { ...this.settings };
  }

  public setAnimationConfig(type: NotificationType, theme: 'light' | 'dark', config: Partial<NotificationAnimationConfig>): void {
    const existingIndex = this.animationConfigs.findIndex(c => c.type === type && c.theme === theme);
    if (existingIndex >= 0) {
      this.animationConfigs[existingIndex] = { ...this.animationConfigs[existingIndex], ...config };
    } else {
      this.animationConfigs.push({ type, theme, animationFile: theme === 'light' ? brightGeneral : darkGeneral, duration: 7000, ...config });
    }
    this.saveAnimations();
  }

  public getAnimationConfig(type: NotificationType, theme?: 'light' | 'dark'): NotificationAnimationConfig | undefined {
    const themeToUse = theme || this.settings.theme;
    return this.animationConfigs.find(c => c.type === type && c.theme === themeToUse);
  }

  public getAllAnimationConfigs(): NotificationAnimationConfig[] {
    return [...this.animationConfigs];
  }

  public setOnNewNotification(callback: (notification: Notification) => void): void {
    this.onNewNotification = callback;
  }

  public async fetchNotifications(): Promise<Notification[]> {
    try {
      const response = await getMyNotifications(1, 10);
      if (response.code === 20000 && response.notifications) {
        return response.notifications;
      }
      return [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  public async checkForNewNotifications(): Promise<void> {
    if (!this.settings.enabled) return;

    const notifications = await this.fetchNotifications();
    
    // Calculate time threshold based on refresh rate (in seconds)
    const now = new Date();
    const timeThresholdMs = this.settings.interval * 1000; // Convert seconds to milliseconds
    
    // Filter for notifications that are recent (within the refresh interval) and not yet shown
    const alertableNotifications = notifications.filter(notification => {
      const notificationTime = new Date(notification.created_at);
      const timeDifferenceMs = now.getTime() - notificationTime.getTime();
      
      // Show notification if it's within the refresh interval and hasn't been shown yet
      return timeDifferenceMs <= timeThresholdMs && 
             !this.lastNotificationIds.has(notification.id);
    });

    // Update last seen notification IDs for all notifications
    notifications.forEach(notification => {
      this.lastNotificationIds.add(notification.id);
    });

    // Trigger callbacks for new alertable notifications
    alertableNotifications.forEach(async (notification) => {
      if (this.onNewNotification) {
        this.onNewNotification(notification);

        try {
          await markNotificationAsRead(notification.id);
        } catch (error) {
          console.error(`Failed to mark notification ${notification.id} as read:`, error);
        }
      }
    });
  }

  public startPolling(): void {
    if (!this.settings.enabled) return;

    this.stopPolling(); // Clear any existing interval

    // Initial fetch
    this.checkForNewNotifications();

    // Set up polling
    this.intervalId = setInterval(() => {
      this.checkForNewNotifications();
    }, this.settings.interval * 1000);
  }

  public stopPolling(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  public enable(): void {
    this.settings.enabled = true;
    this.saveSettings();
    this.startPolling();
  }

  public disable(): void {
    this.settings.enabled = false;
    this.saveSettings();
    this.stopPolling();
  }

  public isEnabled(): boolean {
    return this.settings.enabled;
  }

  public getInterval(): number {
    return this.settings.interval;
  }

  public setInterval(interval: number): void {
    this.settings.interval = Math.max(5, interval); // Minimum 5 seconds
    this.saveSettings();
    
    // Restart polling with new interval
    this.stopPolling();
    if (this.settings.enabled) {
      this.startPolling();
    }
  }

}

export const toNotificationType = (notificationType: string | undefined): NotificationType => {
  switch (notificationType) {
    case 'OPP_NEW_DONATION':
      return 'donation';
    case 'USER_TAGGED':
      return 'challenge';
    default:
      return 'general';
  }
};

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;
